/**
 * ==========================================================================
 *  SIMA - the toll at the door of the juhlasali
 * ==========================================================================
 *  The last room has a crowd in it. Half the venue is standing about on the
 *  floor a stride past the baari waiting for the finaali to start, drink in
 *  hand, and they are standing across the one thing Kimmo has to get through:
 *  the runway, both of its lanes at once, which is the only way to his own
 *  chair.
 *
 *  They are not being difficult. They are being Finnish about it:
 *
 *      "Et kai sä 'Kimmo' selvinpäin tullut pelaamaan? Ota vaikka yks sima
 *       eka."
 *
 *  So the toll is a drink, and the drink is two presses of F: one takes a
 *  glass off the stack on the little table at the end of the baari, and the
 *  other puts a hana over it. Neither does the other one's job.
 *
 *  Only the right-hand PANIMOSIMA tap counts; the crowd asked for sima.
 *
 *  ------------------------------------------------------------------------
 *   WHAT IT COSTS IS THE POUR, NOT THE WALK
 *  ------------------------------------------------------------------------
 *  This used to be built the other way round. The glass stood a metre up
 *  pöytä 2, out of reach of the racing band, and the errand was priced as a
 *  detour: north off the line to the table, back down and west to the
 *  counter, and only then east down the hall. The pour itself was free.
 *
 *  It is now a stack of empties on a side table beside the hanat, and what
 *  costs is `pourDuration` - two seconds of glass under a nozzle, spent at
 *  the counter. Three things are better for it:
 *
 *    - it is findable. Both halves of the errand are one piece of furniture
 *      apart, in the first five metres of the room, and the people who sent
 *      him for it are standing at the end of the same five metres - a player
 *      who walks up to a bar with the crowd's line still on screen is looking
 *      straight at the thing he has been sent for and at the reason. The old
 *      glass was four metres past the taps, up a table full of monitors, and
 *      the honest report from playing it was that nobody found it without
 *      being told.
 *    - a pour is a thing that takes time. Everybody knows how long a pint
 *      takes; nobody has any intuition about how far off a racing line a
 *      glass is. The cost reads off the screen now - the beer climbs the
 *      glass in the corner of it - instead of having to be inferred from a
 *      route.
 *    - it is still a line rather than a button. The stack is *east* of the
 *      hanat, so the run overshoots the counter, turns, and comes back two
 *      metres west to pour, with the whole hall still in front of it - and
 *      what the overshoot ends at is the wall of people, which is what makes
 *      the turn read as a turn rather than as a mistake.
 *
 *  ------------------------------------------------------------------------
 *   ALL THREE NUMBERS ARE HASHED
 *  ------------------------------------------------------------------------
 *  They decide where the run has to go, how close it has to get and how long
 *  it has to stay, which makes them physics in the only sense that matters
 *  here. Moving any of them retires the leaderboard exactly the way moving
 *  `PLAYER.maxSpeed` does. See `src/config/CLAUDE.md`.
 * ==========================================================================
 */

export const SIMA = {
  /** Right-hand nozzle when facing the counter (+z). */
  tapIndex: 0,
  /**
   * How close Kimmo has to get to the glass, and to a nozzle, in metres -
   * measured from the eye, the way `KLAPI` and `INTERACT` both measure.
   *
   * One number for both, because they are the same gesture: something on a
   * surface at hip height that you put a hand out for without stopping. Two
   * numbers would be a claim that reaching over a table and reaching over a
   * counter are different reaches, which nobody has ever felt.
   *
   * It has a second job now that the pour takes time: it is also the leash.
   * The glass fills while Kimmo is inside this distance of the nozzle he
   * opened and stops filling when he is not, so two metres is how far the
   * counter lets him wander while the tap runs. Wide enough to turn round in
   * and to fidget on the spot, nowhere near wide enough to set off down the
   * hall and have the glass arrive full - or, now that the crowd is four
   * metres east of the counter, to open a handle and walk to them.
   */
  reach: 2.0,

  /**
   * And roughly facing it: cos of the widest angle off the crosshair that
   * still counts (~80 deg).
   *
   * Wider than a doorknob's 65 and narrower than the klapikasa's 100, which
   * is where these two belong. The kasa is met sideways at 30 km/h with your
   * eyes already on the veräjä; a glass on a table and a handle on a counter
   * are both things you turn towards.
   *
   * The cone gates the *press* and never the pour. Once the handle is down
   * the glass is under the nozzle and Kimmo can look wherever he likes -
   * asking a player to hold a crosshair on a tap for two seconds while the
   * camera sways would be a test of the drunk meter rather than of anything
   * this errand is about. Distance keeps him there; aim only starts it.
   */
  minLookDot: Math.cos((80 * Math.PI) / 180),

  /**
   * How long the glass takes to fill, in seconds, counted only while Kimmo is
   * within `reach` of the nozzle he opened.
   *
   * This is the whole toll, so it is worth saying what two seconds is. The
   * juhlasali is about five seconds of running end to end and the run that
   * gets there is a forty-second one; two is long enough that it cannot be
   * absorbed by a tidier line - it is not a corner, it is a wait - and short
   * enough that it is over before impatience turns into suspicion that the
   * key did not register. It is also roughly what the tap sounds like, which
   * is not a coincidence: `hana-auki` runs the length of the pour, so the cue
   * ends when the glass is full rather than a second before it.
   *
   * Leaving early does not empty the glass. Whatever went in stays in and the
   * handle springs back, so an interrupted pour is worth exactly the time it
   * got - the alternative punishes a player for a stumble he did not ask for
   * (this is a room you arrive in drunk) by silently resetting a bar he
   * cannot see the bottom of.
   */
  pourDuration: 2.0,
} as const;
